// 조직 감사 로그 (IMP-28)
//
// "누가 우리 조직 링크를 지웠나" — B2B 가 가장 먼저 묻는 것이다.
// OWNER·ADMIN 만 볼 수 있고, 서버가 자기 테넌트로 한정한다.
import { useCallback, useEffect, useMemo, useState } from "react";
import { tenantAuditApi, type AuditItem } from "../api/tenantAudit.ts";
import {
  ApiError,
  AuditPager,
  AuditRangePicker,
  auditActionLabel,
  auditRangeSummary,
  auditRangeToQuery,
  groupAuditActions,
  DEFAULT_AUDIT_RANGE,
  type AuditRange,
} from "@taplink/web-shared";

/**
 * 이 화면에서 나올 수 있는 행위.
 *
 * ⚠️ 운영자 화면과 달리 서버가 목록을 주지 않는다(`/actions` 는 운영자 전용이다).
 *    그래서 **조직 범위에서 실제로 일어나는 것만** 여기 적는다 — 표기는
 *    web-shared 의 공용 표를 쓰므로 이름이 두 벌로 갈라지지 않는다.
 *    고를 수 있는데 결과가 늘 0건인 항목(로그인·가입 등 플랫폼 사건)은 넣지 않는다.
 */
const TENANT_ACTIONS = [
  "LINK_CREATED",
  "LINK_UPDATED",
  "LINK_DEACTIVATED",
  "TENANT_INVITED",
  "TENANT_JOINED",
  "TENANT_ROLE_CHANGED",
  "TENANT_MEMBER_REMOVED",
  "TENANT_PLAN_CHANGED",
  "ENTITLEMENT_CHANGED",
  "ADMIN_TENANT_SUSPENDED",
  "ADMIN_TENANT_RESTORED",
];

export function TenantAuditPage() {
  const [items, setItems] = useState<AuditItem[]>([]);
  const [action, setAction] = useState("");
  const [range, setRange] = useState<AuditRange>(DEFAULT_AUDIT_RANGE);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(30);
  const [hasNext, setHasNext] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalIsExact, setTotalIsExact] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // 기간 조각은 range 가 바뀔 때만 다시 만든다 — 매 렌더에서 만들면
  // '최근 30일'의 기준 시각이 조금씩 밀려 useEffect 가 계속 다시 돈다.
  const rangeQuery = useMemo(() => auditRangeToQuery(range), [range]);

  const load = useCallback(async (a: string, p: number, s: number, r: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantAuditApi.search(a || undefined, p, s, r);
      setItems(res.items);
      setHasNext(res.hasNext);
      setTotal(res.total);
      setTotalIsExact(res.totalIsExact);
    } catch (e) {
      // 403 이면 권한이 없는 것이다 — 서버 문구를 그대로 보여준다
      setError(e instanceof ApiError ? e.message : "불러오지 못했습니다.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(action, page, size, rangeQuery);
  }, [load, action, page, size, rangeQuery]);

  const groups = groupAuditActions(TENANT_ACTIONS);

  return (
    <>
      <h1>활동 기록</h1>
      <p className="hint">
        이 작업 공간에서 일어난 변경입니다. 조회는 남기지 않고 <strong>변경만</strong> 기록합니다.
      </p>
      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <section className="panel">
        {/* 기간을 먼저 고르게 둔다 — 무엇을 보고 있는지가 유형보다 먼저다 */}
        <AuditRangePicker
          value={range}
          onChange={(next) => {
            setPage(0);
            setRange(next);
          }}
        />

        <div className="field-row">
          <div className="field">
            <label htmlFor="audit-action">유형</label>
            <select
              id="audit-action"
              value={action}
              onChange={(e) => {
                setPage(0);
                setAction(e.target.value);
              }}
            >
              <option value="">전체</option>
              {/* 묶어서 보여준다 — 한 줄로 늘어놓으면 찾을 수 없다 */}
              {groups.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.actions.map((a) => (
                    <option key={a} value={a}>
                      {auditActionLabel(a)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <p className="hint audit-range__summary">{auditRangeSummary(range)}</p>
        </div>

        <div className="table-scroll">
          <table className="link-table">
            <caption className="sr-only">작업 공간 활동 기록</caption>
            <thead>
              <tr>
                <th scope="col">시각</th>
                <th scope="col">한 일</th>
                <th scope="col">누가</th>
                <th scope="col">대상</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="hint">{new Date(item.createdAt).toLocaleString("ko-KR")}</td>
                  <td>{auditActionLabel(item.action)}</td>
                  {/* 탈퇴한 사람은 서버가 익명화한 값을 준다 */}
                  <td className="truncate">{item.actorLoginId ?? "—"}</td>
                  <td className="truncate hint">{item.target ?? "—"}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && !error && (
                <tr><td colSpan={4} className="hint">기록이 없습니다.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* .row-actions 는 **표 행 안의** 버튼 묶음이다. 쪽 이동에 쓰면 표에 딱 붙어
            마지막 행처럼 읽힌다 — 링크 목록과 같은 .pager 를 쓴다(구분선·여백 포함) */}
        <AuditPager
          page={page}
          size={size}
          total={total}
          totalIsExact={totalIsExact}
          hasNext={hasNext}
          loading={loading}
          onPage={setPage}
          onSize={(s) => {
            setPage(0);
            setSize(s);
          }}
        />
      </section>
    </>
  );
}
